import { useEffect, useState } from "react";
import styled from "styled-components";
import { paymentMethods } from "../../utils/global_const";
import { useTranslation } from "react-i18next";
import card from "../../assets/icons/credit-card.svg";
import paypal from "../../assets/icons/paypal.svg";
import cash from "../../assets/icons/cash.svg";
import {
  SectionCard,
  SectionHeader,
  SectionTitle,
  SectionBody,
  StepBadge,
  StepBadgeContent,
} from "../checkout/CheckoutSection";

const OptionGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: var(--spacing-sm);

  @media (min-width: 768px) {
    grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
  }
`;

const OptionCard = styled.div`
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
  padding: var(--spacing-md);
  border-radius: var(--border-radius-base);
  border: 2px solid
    ${(props) => (props.$selected ? "var(--primary-100)" : "var(--bg-200)")};
  background-color: ${(props) =>
    props.$selected
      ? "var(--primary-300)"
      : props.$disabled
      ? "var(--bg-200)"
      : "var(--bg-100)"};
  opacity: ${(props) => (props.$disabled ? 0.55 : 1)};
  cursor: ${(props) => (props.$disabled ? "not-allowed" : "pointer")};
  transition: border-color var(--transition-fast),
    background-color var(--transition-fast);

  &:hover {
    border-color: ${(props) =>
      props.$disabled ? "var(--bg-200)" : "var(--primary-200)"};
  }
`;

const IconContainer = styled.div`
  width: 32px;
  height: 32px;
  flex-shrink: 0;
`;

const CardIcon = styled.img`
  width: 100%;
  height: 100%;
`;

const OptionText = styled.span`
  font-size: var(--font-size-base);
  color: var(--text-100);
`;

const UnavailableTag = styled.span`
  display: block;
  font-size: var(--font-size-small);
  color: var(--text-200);
`;

const Payment = ({ setPayment }) => {
  const { t } = useTranslation();
  const [selectedMethod, setSelectedMethod] = useState(null);

  useEffect(() => {
    setPayment(selectedMethod);
  }, [selectedMethod]);

  const getIcon = (methodName) => {
    switch (methodName) {
      case paymentMethods[1].code:
        return card;
      case paymentMethods[0].code:
        return cash;
      default:
        return card;
    }
  };

  return (
    <SectionCard>
      <SectionHeader>
        <StepBadge $done={!!selectedMethod}>
          <StepBadgeContent step={3} done={!!selectedMethod} />
        </StepBadge>
        <SectionTitle>{t("PAYMENT.TITLE")}</SectionTitle>
      </SectionHeader>
      <SectionBody>
        <OptionGrid>
          {paymentMethods.map((method) => {
            const isDisabled = !method.available;
            const isSelected = selectedMethod === method.code;

            return (
              <OptionCard
                key={method.code}
                $selected={isSelected}
                $disabled={isDisabled}
                onClick={() => {
                  if (!isDisabled) {
                    setSelectedMethod(method.code);
                  }
                }}
                role="radio"
                aria-checked={isSelected}
                aria-disabled={isDisabled}
                tabIndex={isDisabled ? -1 : 0}
                onKeyDown={(e) => {
                  if (!isDisabled && (e.key === "Enter" || e.key === " ")) {
                    e.preventDefault();
                    setSelectedMethod(method.code);
                  }
                }}
              >
                <IconContainer>
                  <CardIcon
                    src={getIcon(method.code)}
                    alt={t(`PAYMENT_METHOD.${method.code.toUpperCase()}`)}
                  />
                </IconContainer>
                <div>
                  <OptionText>
                    {t(`PAYMENT_METHOD.${method.code.toUpperCase()}`)}
                  </OptionText>
                  {isDisabled && (
                    <UnavailableTag>
                      {t("PAYMENT_METHOD.UNAVAILABLE")}
                    </UnavailableTag>
                  )}
                </div>
              </OptionCard>
            );
          })}
        </OptionGrid>
      </SectionBody>
    </SectionCard>
  );
};

export default Payment;
